export const labelsEn = {
  title: 'Labels & product marking',
  subtitle: 'Versioned multilingual labels based on released product data.',
  active: 'Active',
  inactive: 'Inactive',
  autoReleased: 'Latest released version',
  help: { open: 'Open label help' },
  tabs: {
    templates: 'Templates',
    formats: 'Formats',
    prints: 'Print history',
    design: 'Design',
    previewPrint: 'Preview & print',
  },
  templates: {
    title: 'Label templates',
    description:
      'The label mode reuses the central PDF designer and freezes every released layout.',
    create: 'New template',
    createDescription: 'Start with a validated physical format and a production-ready base layout.',
  },
  formats: {
    title: 'Label formats',
    description: 'Configure sheets, rolls, grids, margins and gaps in millimetres.',
    create: 'New format',
    edit: 'Edit format',
  },
  prints: {
    title: 'Print history',
    description: 'Immutable test, production and reprint evidence stored in the DMS.',
  },
  designer: {
    subtitle: 'Template version {{version}}',
    elements: 'Elements',
    canvas: 'Label canvas',
    inspector: 'Inspector',
    selectElement: 'Select an element to edit it.',
  },
  preview: {
    title: 'Data snapshot',
    description:
      'Select real ERP source data. The server validates and renders the same snapshot used for printing.',
    legalTitle: 'Review required',
    legalWarning:
      'Declarations and legal label content must be checked and explicitly released by qualified staff before binding use.',
    pdf: 'Server PDF preview',
    empty: 'Select data and generate a preview.',
  },
  fields: {
    name: 'Name',
    description: 'Description',
    mediaType: 'Media type',
    active: 'Active',
    pageWidthMm: 'Page width (mm)',
    pageHeightMm: 'Page height (mm)',
    labelWidthMm: 'Label width (mm)',
    labelHeightMm: 'Label height (mm)',
    columns: 'Columns',
    rows: 'Rows',
    marginLeftMm: 'Left margin (mm)',
    marginTopMm: 'Top margin (mm)',
    horizontalGapMm: 'Horizontal gap (mm)',
    verticalGapMm: 'Vertical gap (mm)',
    format: 'Format',
    text: 'Text',
    dynamicField: 'Dynamic data field',
    analysisParameter: 'Analysis parameter key',
    symbology: 'Barcode symbology',
    valueTemplate: 'Value template',
    gs1Segments: 'GS1 segments',
    gs1Hint: 'Format: AI:source:fixed length, e.g. 01:product.number:14, 10:batch.number',
    xMm: 'X (mm)',
    yMm: 'Y (mm)',
    widthMm: 'Width (mm)',
    heightMm: 'Height (mm)',
    rotation: 'Rotation',
    fontSize: 'Font size (pt)',
    bold: 'Bold',
    languages: 'Languages',
    product: 'Product',
    batch: 'Batch',
    productionOrder: 'Production order',
    declaration: 'Declaration',
    analysis: 'Analysis / nutrition',
    quantity: 'Quantity',
    unit: 'Unit',
    copies: 'Copies',
    reason: 'Reason',
    note: 'Review note',
  },
  columns: {
    name: 'Name',
    version: 'Version',
    format: 'Format',
    status: 'Status',
    updated: 'Updated',
    page: 'Page',
    grid: 'Grid',
    number: 'Print no.',
    product: 'Product / batch',
    template: 'Template',
    kind: 'Kind',
    created: 'Created',
    actions: 'Actions',
  },
  media: { SHEET: 'Sheet', ROLL: 'Roll' },
  versionStatuses: {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    APPROVED: 'Released',
    ARCHIVED: 'Archived',
  },
  printKinds: { TEST: 'Test print', PRODUCTION: 'Production print', REPRINT: 'Reprint' },
  printStatuses: { PENDING: 'Processing', READY: 'Ready', FAILED: 'Failed' },
  elementTypes: {
    TEXT: 'Text',
    DYNAMIC_TEXT: 'Dynamic text',
    BARCODE: 'Barcode',
    QR_CODE: 'QR code',
    LINE: 'Line',
    BOX: 'Box',
  },
  dynamicFields: {
    product: {
      number: 'Product number',
      ean: 'EAN / GTIN',
      name: 'Product name',
      externalName: 'External name',
      declarationName: 'Declaration name',
    },
    declaration: {
      productName: 'Legal product name',
      ingredients: 'Ingredients',
      allergens: 'Allergens',
      additives: 'Additives',
      warnings: 'Warnings',
    },
    nutrition: { summary: 'Nutrition summary' },
    analysis: { value: 'Analysis value' },
    batch: {
      number: 'Batch number',
      bestBeforeDate: 'Best-before date',
      expiryDate: 'Expiry date',
      productionDate: 'Production date',
    },
    productionOrder: { number: 'Production order number' },
    quantity: { value: 'Quantity', unit: 'Unit' },
    storage: { instructions: 'Storage instructions' },
  },
  actions: {
    addElement: 'Add element',
    moveUp: 'Move up',
    moveDown: 'Move down',
    newVersion: 'New version',
    submit: 'Submit',
    approve: 'Release',
    archive: 'Archive',
    preview: 'Preview',
    testPrint: 'Test print',
    productionPrint: 'Production print',
    download: 'Open PDF',
    reprint: 'Reprint',
  },
  transition: {
    submit: {
      title: 'Submit for review',
      description: 'The layout is locked for editing during review.',
    },
    approve: {
      title: 'Release label',
      description: 'Four-eyes approval freezes the version for production prints.',
    },
    archive: {
      title: 'Archive version',
      description: 'Archived versions and their print evidence remain immutable.',
    },
  },
  copy: {
    title: 'Create a new version',
    description: 'The selected version is copied to a new editable draft.',
  },
  reprint: {
    title: 'Reasoned reprint',
    description:
      'The historical version and data snapshot are reused exactly. State why another print is required.',
  },
  messages: {
    invalid: 'Please check the entered data.',
    formatSaved: 'The format was saved.',
    templateCreated: 'The template was created.',
    saved: 'The draft was saved.',
    submit: 'The version was submitted.',
    approve: 'The version was released.',
    archive: 'The version was archived.',
    versionCreated: 'A new draft version was created.',
    printCreated: 'Print {{number}} was generated.',
    reprintCreated: 'The reprint was recorded.',
    productRequired: 'Select a product first.',
    loadFailed: 'The label template could not be loaded.',
  },
} as const;
